import React, { useState, useEffect } from 'react';
import { ItemList } from './ItemList';
import { extractSuffixFromField, sendRequest } from "./vmUtils";
import { lambdaURL } from "../config";

export const VMAssemblyMembers = ({ assembly, user_uuid }) => {
  const [currentMembers, setCurrentMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [bulkInvites, setBulkInvites] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCurrentMembers();
    if (assembly?.isPrivate) {
      fetchPendingInvites();
    }
  }, [assembly]);

  const fetchCurrentMembers = async () => {
    let jsonData = {
      command: "getAssemblyMembers",
      assemblyId: extractSuffixFromField(assembly.SK, 'ASSEMBLY')
    };
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (parsedBody && Array.isArray(parsedBody)) {
          setCurrentMembers(parsedBody);
          setIsLoading(false);
        }
      }
    });
  };

  const fetchPendingInvites = async () => {
    let jsonData = {
      command: "getAssemblyInvites",
      assemblyId: extractSuffixFromField(assembly.SK, 'ASSEMBLY')
    };
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (Array.isArray(parsedBody)) {
          setPendingInvites(parsedBody);
        }
      }
    });
  };

  const handleBulkInvite = async () => {
    if (!bulkInvites.trim()) return;

    const emails = bulkInvites
      .split(/[\n,]/)
      .map(email => email.trim())
      .filter(email => {
        if (!email) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      });

    if (emails.length === 0) {
      setError("No valid emails found");
      return;
    }

    let jsonData = {
      command: "addBatchAssemblyInvites",
      assemblyId: extractSuffixFromField(assembly.SK, 'ASSEMBLY'),
      emails: emails
    };
    
    try {
      await sendRequest(lambdaURL, jsonData);
      setBulkInvites('');
      fetchPendingInvites();
      setError(null);
    } catch (err) {
      setError("Failed to send invites: " + err.message);
    }
  };

  const handleRemoveInvite = async (email) => {
    let jsonData = {
      command: "removeAssemblyInvite",
      assemblyId: extractSuffixFromField(assembly.SK, 'ASSEMBLY'),
      email: email
    };

    try {
      await sendRequest(lambdaURL, jsonData);
      fetchPendingInvites();
    } catch (err) {
      setError("Failed to remove invite: " + err.message);
    }
  };

  const handleRemoveMember = async (memberId) => {
    let jsonData = {
      command: "removeAssemblyMember",
      adminId: user_uuid,
      memberId: memberId,
      assemblyId: extractSuffixFromField(assembly.SK, 'ASSEMBLY')
    };
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        fetchCurrentMembers();
      }
    });
  };

  const renderMember = (member) => (
    <div className="vm-item-container">
      <div className="vm-item-content">
        <div className="vm-item-title">{member.name}</div>
        <div className="vm-item-description">{member.email}</div>
      </div>
      {member.userId !== user_uuid && (
        <button 
          className="vm-base-shadow-button-14"
          onClick={() => handleRemoveMember(member.userId)}
        >
          Remove
        </button>
      )}
    </div>
  );

  if (isLoading) {
    return <div className="vm-loading">Loading member data...</div>;
  }

  return (
    <div className="vm-section">
      <div className="vm-section-title">
        <h2>Assembly Members</h2>
      </div>

      <div className="vm-form-vertical">
        <div className="vm-section-sub-header">
          <b>Current Members ({currentMembers.length})</b>
        </div>
        <ItemList 
          items={currentMembers}
          onItemClick={() => {}}
          titleField="name"
          descriptionField="email"
          idField="userId"
          emptyMessage="No members yet"
          renderItem={renderMember}
        />
      </div>

      {assembly?.isPrivate && (
        <>
          <div className="vm-form-vertical">
            <div className="vm-section-sub-header">
              <b>Pending Invites ({pendingInvites.length})</b>
            </div>
            {pendingInvites.length > 0 && (
              <div className="space-y-2">
                {pendingInvites.map((email) => (
                  <div key={email} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span>{email}</span>
                    <button
                      onClick={() => handleRemoveInvite(email)}
                      className="vm-base-shadow-button-14"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="vm-form-vertical">
            <div className="vm-section-sub-header">
              <b>Invite Members</b>
            </div>
            {error && <div className="vm-form-error">{error}</div>}
            <textarea
              className="vm-form-textarea"
              value={bulkInvites}
              onChange={(e) => setBulkInvites(e.target.value)}
              placeholder="Enter email addresses (one per line or comma-separated)"
              rows="4"
            />
            <button
              onClick={handleBulkInvite}
              disabled={!bulkInvites.trim()}
            >
              Save Invites
            </button>
          </div>
        </>
      )}
    </div>
  );
};